// frontend/src/pages/AdminBulkRegister.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_URL } from "../config";
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Paper,
  Alert,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  FormControlLabel,
  Switch,
  Tab,
  Tabs,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from "@mui/material";
import { Send, Download, ContentCopy, Done } from "@mui/icons-material";

// セキュアなパスワード生成関数（12文字以上・4種の文字クラスを必ず含む）
const generateSecurePassword = () => {
  const length = 12;
  const lowers = "abcdefghijklmnopqrstuvwxyz";
  const uppers = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const digits = "0123456789";
  const specials = "!@#$%^&*()_+[]{}-=~";
  const all = lowers + uppers + digits + specials;

  const pick = (pool) => pool[Math.floor(Math.random() * pool.length)];
  const getRandomInt = (max) => {
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
      const buf = new Uint32Array(1);
      crypto.getRandomValues(buf);
      return buf[0] % max;
    }
    return Math.floor(Math.random() * max);
  };

  const chars = [pick(lowers), pick(uppers), pick(digits), pick(specials)];
  for (let i = chars.length; i < length; i++) {
    chars.push(all[getRandomInt(all.length)]);
  }
  // フィッシャー–イェーツでシャッフル
  for (let i = chars.length - 1; i > 0; i--) {
    const j = getRandomInt(i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join("");
};

const AdminBulkRegister = () => {
  const [emails, setEmails] = useState("");
  const [inputMode, setInputMode] = useState("email"); // 'email' | 'studentId'
  const [affiliation, setAffiliation] = useState(""); // 'musashino' | 'otsuma'
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    severity: "success",
  });
  const [clipboardSuccess, setClipboardSuccess] = useState(false);
  const [sendEmailsLoading, setSendEmailsLoading] = useState(false);
  const [includeHeader, setIncludeHeader] = useState(true);
  const [subjects, setSubjects] = useState([]); // 科目一覧
  const [selectedSubjectIds, setSelectedSubjectIds] = useState([]); // 選択された科目ID

  // 科目一覧を取得
  const fetchSubjects = async () => {
    try {
      const res = await axios.get(`${API_URL}/subjects/`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setSubjects(res.data);
    } catch (error) {
      console.error("科目一覧の取得に失敗しました", error);
      setNotification({
        show: true,
        message: "科目一覧の取得に失敗しました",
        severity: "error",
      });
    }
  };

  useEffect(() => {
    fetchSubjects();
  }, []);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const extractErrorMessage = (err) => {
    const detail = err?.response?.data?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
      return detail
        .map((d) => {
          const where = Array.isArray(d?.loc) ? ` [${d.loc.join('.')}]` : '';
          return `${d?.msg || '入力エラー'}${where}`;
        })
        .join('; ');
    }
    if (detail && typeof detail === 'object') {
      try { return JSON.stringify(detail); } catch { /* noop */ }
    }
    return err?.message || "登録に失敗しました";
  };

  const buildEmailsFromInput = () => {
    if (inputMode === "email") {
      return emails
        .split("\n")
        .map((email) => email.trim())
        .filter((email) => email);
    }
    // studentId mode
    if (!affiliation) {
      throw new Error("所属を選択してください");
    }
    const domain = affiliation === "musashino" ? "stu.musashino-u.ac.jp" : "cst.otsuma.ac.jp";
    const ids = emails
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line);
    const generated = ids.map((raw) => {
      const digits = (raw.match(/\d+/g) || []).join("");
      if (digits.length !== 7) {
        throw new Error(`学籍番号は7桁で入力してください: ${raw}`);
      }
      return `s${digits}@${domain}`;
    });
    return generated;
  };

  const handleBulkRegistration = async () => {
    if (!emails.trim()) {
      setNotification({
        show: true,
        message: inputMode === "email" ? "メールアドレスを入力してください" : "学籍番号を入力してください",
        severity: "error",
      });
      return;
    }

    setLoading(true);

    try {
      const emailList = buildEmailsFromInput();

      // 結果を格納する配列
      const registrationResults = {
        success: [],
        fail: [],
      };

      // 各メールアドレスごとに処理
      for (const email of emailList) {
        try {
          // セキュアなパスワードを生成
          const password = generateSecurePassword();

          // メールアドレスのローカル部分をユーザー名として取得
          const username = email.split("@")[0];

          // ユーザー登録API呼び出し
          await axios.post(
            `${API_URL}/users/register`,
            {
              email: email,
              password: password,
              full_name: username,
              role: "student",
              subject_ids: selectedSubjectIds.map(Number), // 数値化して送信
            },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          );

          // 成功した場合、パスワードと共に記録
          registrationResults.success.push({
            email,
            password,
            username,
          });
        } catch (err) {
          // 失敗した場合、エラーメッセージと共に記録
          registrationResults.fail.push({
            email,
            error: extractErrorMessage(err),
          });
        }
      }

      setResults(registrationResults);

      // 通知メッセージを作成
      const successCount = registrationResults.success.length;
      const failCount = registrationResults.fail.length;
      const resultMessage = `${successCount}件登録成功、${failCount}件失敗`;

      setNotification({
        show: true,
        message: resultMessage,
        severity: failCount > 0 ? "warning" : "success",
      });

      // 入力フィールドをクリア
      setEmails("");
      setSelectedSubjectIds([]); // 選択された科目もクリア

      // 結果タブに自動的に切り替え
      if (successCount > 0) {
        setTabValue(1);
      }
    } catch (err) {
      const msg = extractErrorMessage(err) || "処理中にエラーが発生しました";
      setNotification({
        show: true,
        message: msg,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // ユーザー一覧をCSVとしてダウンロード
  const handleDownloadCSV = () => {
    if (!results || results.success.length === 0) return;

    const header = includeHeader
      ? "メールアドレス,パスワード,ユーザー名\n"
      : "";

    const csvContent =
      header +
      results.success
        .map((user) => `${user.email},${user.password},${user.username}`)
        .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `学生アカウント一覧_${new Date().toISOString().slice(0, 10)}.csv`
    );
    link.style.visibility = "hidden";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // クリップボードにコピー
  const handleCopyToClipboard = () => {
    if (!results || results.success.length === 0) return;

    const header = includeHeader
      ? "メールアドレス\tパスワード\tユーザー名\n"
      : "";

    const textContent =
      header +
      results.success
        .map((user) => `${user.email}\t${user.password}\t${user.username}`)
        .join("\n");

    navigator.clipboard.writeText(textContent).then(() => {
      setClipboardSuccess(true);
      setTimeout(() => setClipboardSuccess(false), 2000);
    });
  };

  // メール一斉送信処理
  const handleSendEmails = async () => {
    if (!results || results.success.length === 0) return;

    setSendEmailsLoading(true);

    try {
      // メール送信APIを呼び出す
      await axios.post(
        `${API_URL}/users/send-credentials`,
        {
          users: results.success.map((user) => ({
            email: user.email,
            password: user.password,
            full_name: user.username,
          })),
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );

      setNotification({
        show: true,
        message: `${results.success.length}件のアカウント情報をメール送信しました`,
        severity: "success",
      });
    } catch (err) {
      setNotification({
        show: true,
        message: "メール送信中にエラーが発生しました",
        severity: "error",
      });
    } finally {
      setSendEmailsLoading(false);
    }
  };

  return (
    <Container>
      <Paper sx={{ p: 3, mt: 3, mb: 4, backgroundColor: "#000" }}>
        <Typography variant="h5" component="h2" gutterBottom>
          学生一括登録
        </Typography>

        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="登録タブ"
          sx={{ mb: 2 }}
        >
          <Tab label="登録情報入力" />
          <Tab label="登録結果" disabled={!results} />
        </Tabs>

        {notification.show && (
          <Alert
            severity={notification.severity}
            sx={{ mb: 2 }}
            onClose={() => setNotification({ ...notification, show: false })}
          >
            {notification.message}
          </Alert>
        )}

        {tabValue === 0 && (
          <Box>
            <Typography variant="body2" sx={{ mb: 2 }}>
              以下のいずれかの方法で一括登録できます。自動的にセキュアなパスワードが生成されます。氏名はユーザー名部分が自動的に使用されます。
            </Typography>

            {/* 入力モード切替 */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="input-mode-label">入力方法</InputLabel>
              <Select
                labelId="input-mode-label"
                value={inputMode}
                label="入力方法"
                onChange={(e) => setInputMode(e.target.value)}
              >
                <MenuItem value="email">メールアドレスをそのまま入力</MenuItem>
                <MenuItem value="studentId">学籍番号からメールを自動生成</MenuItem>
              </Select>
            </FormControl>

            {inputMode === "studentId" && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="affiliation-label">所属</InputLabel>
                <Select
                  labelId="affiliation-label"
                  value={affiliation}
                  label="所属"
                  onChange={(e) => setAffiliation(e.target.value)}
                >
                  <MenuItem value={"musashino"}>武蔵野大学データサイエンス学部</MenuItem>
                  <MenuItem value={"otsuma"}>大妻女子大学</MenuItem>
                </Select>
              </FormControl>
            )}

            <TextField
              label={inputMode === "email" ? "メールアドレス（改行区切り）" : "学籍番号（改行区切り）"}
              multiline
              rows={8}
              fullWidth
              value={emails}
              onChange={(e) => setEmails(e.target.value)}
              placeholder={
                inputMode === "email"
                  ? `例：\ns1234567@stu.musashino-u.ac.jp\ns2345678@stu.musashino-u.ac.jp`
                  : `例：\n1234567\n2345678\n（所属：武蔵野大学データサイエンス学部 → s1234567@stu.musashino-u.ac.jp 生成）`
              }
              sx={{ mb: 2 }}
            />

            {/* 科目選択（複数選択） */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="subject-select-label">科目</InputLabel>
              <Select
                labelId="subject-select-label"
                multiple
                value={selectedSubjectIds}

                onChange={(e) =>
                  setSelectedSubjectIds(
                    // MUI は string を返すことがあるので明示的に数値化
                    e.target.value.map((v) => Number(v))
                  )
                }
                input={<OutlinedInput label="科目" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip
                        key={value}
                        label={subjects.find(s => s.id === value)?.name || value}
                      />
                    ))}
                  </Box>
                )}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      bgcolor: "#fff",
                      color: "#000",
                      maxHeight: 300
                    }
                  },
                }}
              >
                {subjects.map((subject) => (
                  <MenuItem key={subject.id} value={subject.id}>
                    <Checkbox checked={selectedSubjectIds.indexOf(subject.id) > -1} />
                    <ListItemText primary={subject.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              fullWidth
              onClick={handleBulkRegistration}
              disabled={loading}
              sx={{ mt: 2, py: 1.5 }}
            >
              {loading ? (
                <>
                  <CircularProgress size={24} sx={{ mr: 1, color: "white" }} />
                  処理中...
                </>
              ) : (
                "個別パスワードで一括登録"
              )}
            </Button>
          </Box>
        )}

        {tabValue === 1 && results && (
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mb: 2,
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <Typography variant="h6">
                登録成功: {results.success.length}件 / 失敗:{" "}
                {results.fail.length}件
              </Typography>

              <Box sx={{ display: "flex", gap: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={includeHeader}
                      onChange={(e) => setIncludeHeader(e.target.checked)}
                    />
                  }
                  label="ヘッダー含む"
                />

                <Button
                  variant="outlined"
                  startIcon={<Download />}
                  onClick={handleDownloadCSV}
                  disabled={!results.success.length}
                >
                  CSVダウンロード
                </Button>

                <Button
                  variant="outlined"
                  startIcon={clipboardSuccess ? <Done /> : <ContentCopy />}
                  onClick={handleCopyToClipboard}
                  disabled={!results.success.length}
                  color={clipboardSuccess ? "success" : "primary"}
                >
                  {clipboardSuccess ? "コピー完了" : "クリップボードにコピー"}
                </Button>

                <Button
                  variant="contained"
                  startIcon={<Send />}
                  onClick={handleSendEmails}
                  disabled={!results.success.length || sendEmailsLoading}
                  color="primary"
                >
                  {sendEmailsLoading ? (
                    <>
                      <CircularProgress
                        size={24}
                        sx={{ mr: 1, color: "white" }}
                      />
                      送信中...
                    </>
                  ) : (
                    "メール送信"
                  )}
                </Button>
              </Box>
            </Box>

            {/* 成功した登録の一覧 */}
            {results.success.length > 0 && (
              <>
                <Typography variant="subtitle1" gutterBottom>
                  登録成功アカウント
                </Typography>
                <TableContainer sx={{ mb: 3 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>No.</TableCell>
                        <TableCell>メールアドレス</TableCell>
                        <TableCell>パスワード</TableCell>
                        <TableCell>ユーザー名</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {results.success.map((user, index) => (
                        <TableRow key={index}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>{user.password}</TableCell>
                          <TableCell>{user.username}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {/* 失敗した登録の一覧 */}
            {results.fail.length > 0 && (
              <>
                <Typography variant="subtitle1" gutterBottom>
                  登録失敗アカウント
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>No.</TableCell>
                        <TableCell>メールアドレス</TableCell>
                        <TableCell>エラー</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {results.fail.map((user, index) => (
                        <TableRow key={index}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>{user.error}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default AdminBulkRegister;